import { useState, useEffect } from "react";
import axios from "axios";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  ComposedChart, Area, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ label, value, suffix = "%", invert = false, testId }) => {
  const num = typeof value === "number" ? value : null;
  const good = num !== null && (invert ? num < 0 : num > 0);
  const Icon = num === null || num === 0 ? Minus : num > 0 ? TrendingUp : TrendingDown;
  return (
    <div data-testid={testId} className="bg-white rounded-lg border border-[#e0dbd2] px-4 py-3">
      <p className="text-[10px] font-bold uppercase tracking-widest text-[#8a8278]">{label}</p>
      <p className={cn(
        "text-xl font-extrabold flex items-center gap-1.5 mt-1",
        num === null ? "text-[#8a8278]" : good ? "text-emerald-600" : "text-red-600"
      )}>
        <Icon className="h-4 w-4" />
        {num === null ? "—" : `${num > 0 ? "+" : ""}${num}${suffix}`}
      </p>
    </div>
  );
};

export const PriceCorrelation = ({ categories, refreshKey }) => {
  const [category, setCategory] = useState("");
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!category && categories.length) {
      const preferred = ["Mixer Grinders", "Pressure Cookers", "Gas Stoves", "Wet Grinders"];
      setCategory(preferred.find((p) => categories.includes(p)) || categories[0]);
    }
  }, [categories]);

  useEffect(() => {
    if (category) fetchData();
  }, [category, days, refreshKey]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data: res } = await axios.get(
        `${API}/market-share/price-correlation?category=${encodeURIComponent(category)}&days=${days}`
      );
      setData(res);
    } catch (e) {
      console.error("Correlation fetch failed:", e);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const chartData = (data?.series || []).filter(
    (s) => s.own_avg_price || s.comp_avg_price || s.own_est_units
  );

  return (
    <div data-testid="price-correlation-section" className="bg-white rounded-xl border border-[#e0dbd2] p-5 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-extrabold text-[#1a1814]">Sales vs Price Correlation</h2>
          <p className="text-xs text-[#8a8278]">
            Estimated sales (from BSR) against your prices and competitor prices
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="correlation-category-select"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="text-sm px-3 py-1.5 rounded-lg bg-[#f2f0ec] border border-[#e0dbd2] font-semibold"
          >
            {categories.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <div className="flex rounded-lg bg-[#f2f0ec] border border-[#e0dbd2] p-0.5">
            {[30, 90, 180].map((d) => (
              <button
                key={d}
                data-testid={`correlation-days-${d}`}
                onClick={() => setDays(d)}
                className={cn(
                  "px-2.5 py-1 rounded-md text-xs font-bold",
                  days === d ? "bg-[#1a1814] text-white" : "text-[#8a8278]"
                )}
              >
                {d}d
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <StatCard testId="stat-sales-change" label={`Est. Sales Δ (${days}d)`} value={data?.sales_change_pct} />
        <StatCard testId="stat-own-price-change" label={`Our Price Δ (${days}d)`} value={data?.own_price_change_pct} invert />
        <StatCard testId="stat-comp-price-change" label={`Comp Price Δ (${days}d)`} value={data?.comp_price_change_pct} />
        <StatCard testId="stat-corr-own" label="Corr: Sales vs Our Price" value={data?.correlation_own_price} suffix="" invert />
        <StatCard testId="stat-corr-comp" label="Corr: Sales vs Comp Price" value={data?.correlation_comp_price} suffix="" />
      </div>

      {/* Interpretation */}
      {data?.interpretation?.length > 0 && (
        <div data-testid="correlation-interpretation" className="bg-[#f8f5ee] border border-[#e0dbd2] rounded-lg px-4 py-3 space-y-1.5">
          {data.interpretation.map((line, i) => (
            <p key={i} className="text-xs text-[#1a1814] flex gap-2">
              <span className="text-[#f0a500] font-bold">→</span>
              {line}
            </p>
          ))}
        </div>
      )}

      {/* Chart */}
      {loading ? (
        <div className="h-72 flex items-center justify-center text-sm text-[#8a8278]">Loading...</div>
      ) : chartData.length > 1 ? (
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e0dbd2" />
            <XAxis dataKey="date" tick={{ fontSize: 10 }} minTickGap={40} />
            <YAxis yAxisId="price" tick={{ fontSize: 10 }} tickFormatter={(v) => `₹${v}`} />
            <YAxis yAxisId="units" orientation="right" tick={{ fontSize: 10 }} />
            <Tooltip
              formatter={(v, name) =>
                name.includes("Price") ? [`₹${Number(v).toLocaleString("en-IN")}`, name] : [Number(v).toLocaleString("en-IN"), name]
              }
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Area yAxisId="units" type="monotone" dataKey="own_est_units" name="Est. Units (Own)" fill="#f0a50033" stroke="#f0a500" connectNulls />
            <Line yAxisId="price" type="monotone" dataKey="own_avg_price" name="Our Avg Price" stroke="#1a1814" strokeWidth={2} dot={false} connectNulls />
            <Line yAxisId="price" type="monotone" dataKey="comp_avg_price" name="Comp Avg Price" stroke="#c0392b" strokeWidth={2} strokeDasharray="5 4" dot={false} connectNulls />
          </ComposedChart>
        </ResponsiveContainer>
      ) : (
        <div className="h-32 flex items-center justify-center text-sm text-[#8a8278]">
          Not enough data yet — the series builds with every daily sync.
        </div>
      )}
    </div>
  );
};
